import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Button, Drawer, Dropdown, Input, Table, Tag, DatePicker, Tooltip, message } from "antd";
import {
  UploadOutlined,
  SendOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  DownOutlined,
  ArrowRightOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { openNotificationWithIcon } from "../../utils";
import { otherService } from "../../api";
import { useAuthUser } from "../../hooks";
import dayjs, { Dayjs } from "dayjs";
import "./others-container.scss";

const { RangePicker } = DatePicker;

interface BatchRun {
  runId: string;
  ranBy: string;
  ranByInitials: string;
  date: string;
  totalUids: number;
  progress: number;
  results: { success: number; failed: number; warning: number };
  status: "Completed" | "Partial" | "Pending" | "Failed";
  uids: UidRow[];
}

interface UidRow {
  uid: string;
  status: "Completed" | "Failed" | "Classification Not Found" | "Pending";
  details?: string;
}

const statusColors: Record<string, string> = {
  Completed: "green",
  Partial: "orange",
  Pending: "yellow",
  Failed: "red",
};

const uidStatusIcon: Record<string, React.ReactNode> = {
  Completed: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
  Failed: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
  "Classification Not Found": <WarningOutlined style={{ color: "#faad14" }} />,
  Pending: <ClockCircleOutlined style={{ color:"#FFD700" }} /> ,
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

export const OthersContainer = () => {
  const { firstName, lastName, email } = useAuthUser();
  const fullName = `${firstName} ${lastName}`.trim() || email || "Unknown";

  const [isDragging, setIsDragging] = useState(false);
  const [uploadedUids, setUploadedUids] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [batchRuns, setBatchRuns] = useState<BatchRun[]>([]);
  const [isBatchLoading, setIsBatchLoading] = useState(false);
  const [isUidDrawerOpen, setIsUidDrawerOpen] = useState(false);
  const [activeRun, setActiveRun] = useState<BatchRun | null>(null);
  const [isRefreshingActiveRun, setIsRefreshingActiveRun] = useState(false);
  const [uidSearchTerm, setUidSearchTerm] = useState("");
  const [uidStatusFilter, setUidStatusFilter] = useState("");
  const [searchRunId, setSearchRunId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().subtract(7, "day").startOf("day"),
    dayjs().endOf("day"),
  ]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);

  const resetUidDrawerScroll = () => {
    const drawerBody = document.querySelector(".uid-drawer .ant-drawer-body");
    if (drawerBody instanceof HTMLElement) {
      drawerBody.scrollTo({ top: 0, behavior: "auto" });
    }
  };

  const mapBatchRunStatus = (value: string): BatchRun["status"] => {
    const normalized = (value || "").toLowerCase();
    if (normalized === "completed") return "Completed";
    if (normalized === "partial") return "Partial";
    if (normalized === "failed") return "Failed";
    return "Pending";
  };

  const mapUidStatus = (value: string): UidRow["status"] => {
    const normalized = (value || "").toLowerCase();
    if (normalized === "success" || normalized === "completed") return "Completed";
    if (normalized === "failed") return "Failed";
    if (normalized ==="pending") return "Pending";
    if (
      normalized === "invalid" ||
      normalized === "classification_not_found" ||
      normalized === "not_found"
    ) {
      return "Classification Not Found";
    }
    return "Failed";
  };

  const getUidDetails = (status: UidRow["status"], uidItem: any) => {
    if (status === "Completed") {
      return undefined;
    }

    return uidItem.details || uidItem.message || uidItem.error;
  };

  const mapRuns = (runs: any[]): BatchRun[] => {
    return runs.map((item: any, index: number) => {
      const rawUidRows: any[] = item?.results?.items || [];
      const uidRows: UidRow[] = rawUidRows.map((uidItem: any) => {
        const uidStatus = mapUidStatus(uidItem.status || uidItem.resultStatus || "Pending");
        return {
          uid: uidItem.uid || uidItem.documentUUID || uidItem.documentId || "",
          status: uidStatus,
          details: getUidDetails(uidStatus, uidItem),
        };
      });

      // If API doesn't provide summary counts, calculate them from uidRows
      const summary = item?.results?.summary || {};
      const success = Number(summary.successCount ?? uidRows.filter((u) => u.status === "Completed").length);
      const failed = Number(summary.failedCount ?? uidRows.filter((u) => u.status === "Failed").length);
      const warning = Number(summary.invalidCount ?? uidRows.filter((u) => u.status === "Classification Not Found").length);
      const pending = Number(summary.pendingCount ?? uidRows.filter((u) => u.status === "Pending").length);
      const total = Number(item.totalDocuments ?? uidRows.length);
      const progress = total > 0 ? Math.round(((success + failed + warning) / total) * 100) : 0;

      return {
        runId: item.runId || item.run_id || item.batchRunId || `RUN-${index + 1}`,
        ranBy: item.runBy || item.ranBy || item.ran_by || item.user || "Unknown",
        ranByInitials: getInitials(item.runBy || item.ranBy || item.ran_by || item.user || "Unknown"),
        date: dayjs(item.runDate || item.date || item.createdAt || new Date()).format("DD MMM YYYY, hh:mm A"),
        totalUids: item.totalDocuments || item.totalUids || uidRows.length,
        progress: Number(item.progress ?? progress),
        results: {
          success,
          failed,
          warning,
        },
        status: mapBatchRunStatus(item.status),
        uids: uidRows,
      };
    });
  };

  const fetchBatchRuns = useCallback(async () => {
    setIsBatchLoading(true);
    try {
      const params: {
        fromDate?: string;
        toDate?: string;
        status?: string;
        runId?: string;
      } = {};

      if (statusFilter) {
        params.status = statusFilter;
      }

      const trimmedRunId = searchRunId.trim();
      if (trimmedRunId) {
        params.runId = trimmedRunId;
      }

      if (dateRange) {
        params.fromDate = dateRange[0].format("YYYY-MM-DD HH:mm:ss");
        params.toDate = dateRange[1].format("YYYY-MM-DD HH:mm:ss");
      }

      const response = await otherService.getOthersBatchData(params);
      const runs = response?.data?.items || [];

      const mappedRuns: BatchRun[] = mapRuns(runs);

      setBatchRuns(mappedRuns);
    } catch (error: any) {
      openNotificationWithIcon(
        "Error",
        error?.response?.data?.message || "Failed to fetch batch data.",
        "error"
      );
    } finally {
      setIsBatchLoading(false);
    }
  }, [dateRange, searchRunId, statusFilter]);

  const refreshActiveRunStatus = async () => {
    if (!activeRun?.runId) {
      return;
    }

    setIsRefreshingActiveRun(true);
    try {
      const response = await otherService.getOthersBatchData({ runId: activeRun.runId });
      const refreshedRuns = mapRuns(response?.data?.items || []);
      const refreshedRun = refreshedRuns.find((run) => run.runId === activeRun.runId);

      if (!refreshedRun) {
        openNotificationWithIcon("", "Unable to refresh this run ID right now.", "warning");
        return;
      }

      setActiveRun(refreshedRun);
      setBatchRuns((prevRuns) =>
        prevRuns.map((run) => (run.runId === refreshedRun.runId ? refreshedRun : run))
      );
      // message.success(`Run ID ${refreshedRun.runId} status refreshed.`);
    } catch (error: any) {
      openNotificationWithIcon(
        "Error",
        error?.response?.data?.message || "Failed to refresh run status.",
        "error"
      );
    } finally {
      setIsRefreshingActiveRun(false);
    }
  };

  useEffect(() => {
    fetchBatchRuns();
  }, [fetchBatchRuns]);

  const parseExcel = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const firstColumnValues: string[] = rows
          .map((r) => String(r?.[0] ?? "").trim())
          .filter(Boolean);

        const uids: string[] = firstColumnValues;

      

        if (uids.length === 0) {
          openNotificationWithIcon("", "No document IDs found in the first column.", "warning");
          return;
        }

        setUploadedUids(uids);
        setFileName(file.name);
        openNotificationWithIcon(
          "File loaded",
          `${uids.length} document ID(s) ready to resend.`,
          "success"
        );
      } catch {
        openNotificationWithIcon("", "Failed to parse the Excel file.", "error");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileSelect = (file: File) => {
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      openNotificationWithIcon("", "Only .xlsx / .xls files are supported.", "error");
      return;
    }
    parseExcel(file);
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, []);

  const handleResendBulk = async () => {
    if (uploadedUids.length === 0) {
      openNotificationWithIcon("", "Please upload an Excel file first.", "warning");
      return;
    }
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    try {
      const response = await otherService.resendBulkClassification({
        documentUUIDs: uploadedUids,
        ranBy: fullName,
        totalDocuments: uploadedUids.length,
      });

      message.success(response?.message || "Bulk resend request submitted successfully.");
      await fetchBatchRuns();

      setUploadedUids([]);
      setFileName("");
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to resend classification.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openUidDrawer = (run: BatchRun) => {
    setActiveRun(run);
    setUidSearchTerm("");
    setUidStatusFilter("");
    setIsUidDrawerOpen(true);

    // Ensure the drawer starts from top even when user clicks from lower page area.
    pageContainerRef.current?.scrollIntoView({ block: "start", behavior: "auto" });
  };

  const closeUidDrawer = () => {
    setIsUidDrawerOpen(false);
  };

  const batchColumns = [
    {
      title: "RUN ID",
      dataIndex: "runId",
      key: "runId",
      align: "center" as const,
      render: (id: string, record: BatchRun) => (
        <button
          type="button"
          className="run-id-link-btn"
          onClick={() => openUidDrawer(record)}
        >
          {id}
        </button>
      ),
    },
    { title: "DATE", dataIndex: "date", key: "date", align: "center" as const },
    {
      title: (
        <Dropdown
          trigger={["click"]}
          menu={{
            selectable: true,
            selectedKeys: [statusFilter || "ALL"],
            items: [
              { key: "ALL", label: "All" },
              { key: "COMPLETED", label: "Completed" },
              { key: "PARTIAL", label: "Partial" },
              { key: "PENDING", label: "Pending" },
              { key: "FAILED", label: "Failed" },
            ],
            onClick: ({ key }) => {
              setStatusFilter(key === "ALL" ? "" : String(key));
            },
          }}
        >
          <button
            type="button"
            className="status-header-filter-btn"
            onClick={(event) => event.preventDefault()}
            aria-label="Filter batch status"
          >
            <span>STATUS</span>
            <DownOutlined />
          </button>
        </Dropdown>
      ),
      key: "status",
      dataIndex: "status",
      align: "center" as const,
      width: 120,
      render: (_: any, record: BatchRun) => (
        <Tag color={statusColors[record.status]}>{record.status}</Tag>
      ),
    },
    {
      title: "ACTION",
      key: "action",
      align: "center" as const,
     
      render: (_: any, record: BatchRun) => (
        <Tooltip title="View UID details">
          <Button
            type="text"
            icon={<ArrowRightOutlined />}
            className="view-uids-btn"
            onClick={() => openUidDrawer(record)}
            aria-label={`View UIDs for ${record.runId}`}
          />
        </Tooltip>
      ),
    },
  ];

  const uidColumns = [
    { title: "UUID", dataIndex: "uid", key: "uid", className: "uid-cell text-wrapper", align: "center" as const },
    {
      title: (
        <Dropdown
          trigger={["click"]}
          menu={{
            selectable: true,
            selectedKeys: [uidStatusFilter || "ALL"],
            items: [
              { key: "ALL", label: "All" },
              { key: "Completed", label: "Completed" },
              { key: "Failed", label: "Failed" },
             
              { key: "Pending", label: "Pending" },
            ],
            onClick: ({ key }) => {
              setUidStatusFilter(key === "ALL" ? "" : String(key));
            },
          }}
        >
          <button
            type="button"
            className="status-header-filter-btn"
            onClick={(event) => event.preventDefault()}
            aria-label="Filter UID status"
          >
            <span>STATUS</span>
            <DownOutlined />
          </button>
        </Dropdown>
      ),
      key: "status",
      align: "center" as const,
      render: (_: any, record: UidRow) => (
        <span className="uid-status-cell">
          {uidStatusIcon[record.status]}
          &nbsp;
          <span style={{ color: record.status === "Failed" ? "#ff4d4f" : record.status === "Classification Not Found" ? "hsl(40, 43%, 30%)" : record.status==="Pending" ? "#FFD700" : "#52c41a" }}>
            {record.status}
          </span>
        </span>
      ),
    },
    {
      title: "DETAILS",
      dataIndex: "details",
      key: "details",
      align: "center" as const,
      render: (d: string) => d || "—",
    },
  ];

  const filteredUidRows = useMemo(() => {
    const rows = activeRun?.uids || [];
    const normalizedSearch = uidSearchTerm.trim().toLowerCase();

    return rows.filter((row) => {
      const matchesSearch =
        !normalizedSearch ||
        row.uid.toLowerCase().includes(normalizedSearch) ||
        (row.details || "").toLowerCase().includes(normalizedSearch);

      const matchesStatus = !uidStatusFilter || row.status === uidStatusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [activeRun, uidSearchTerm, uidStatusFilter]);

  return (
    <div className="others-container" ref={pageContainerRef}>
      {/* Upload Section */}
      <div className="others-section upload-hero-section">
        <div
          className={`drop-zone ${isDragging ? "dragging" : ""} ${fileName ? "has-file" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="drop-zone-inner-border" />
          <div className="drop-icon-shell">
            <UploadOutlined className="drop-icon" />
          </div>
          <h2 className="drop-title">Upload Excel Document</h2>
          <p className="drop-description">
            Drag and drop your spreadsheet here to initiate a new bulk UUID extraction
            Ensure the first column contains the document IDs (UUIDs) you wish to process.
          </p>
          <Button
            className="browse-file-btn"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Browse Files
          </Button>
          {fileName && <p className="drop-selected-file">{fileName}</p>}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
              e.target.value = "";
            }}
          />
        </div>

        <div className="upload-actions">
          <Button
            type="primary"
            icon={<SendOutlined />}
            disabled={uploadedUids.length === 0 || isSubmitting}
            onClick={handleResendBulk}
            className="resend-btn"
          >
            Resend Bulk Classification
          </Button>
        </div>
      </div>

      {/* Batch Runs Section */}
      <div className="others-section batch-section">
        <div className="batch-header">
          <div>
            <h2 className="section-title">Batch Runs</h2>
            <p className="section-subtitle">Click Action to view individual UID statuses</p>
          </div>
          <div className="batch-filters">
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchBatchRuns}
              loading={isBatchLoading}
            >
              Reload
            </Button>
           
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search run ID..."
              value={searchRunId}
              onChange={(e) => setSearchRunId(e.target.value)}
              className="run-search"
            />
            <RangePicker
              showTime={{
                use12Hours: true,
                format: "h:mm A",
              }}
              format="YYYY-MM-DD hh:mm A"
              value={dateRange as [Dayjs, Dayjs]}
              onChange={(dates) =>
                setDateRange(dates ? [dates[0] as Dayjs, dates[1] as Dayjs] : null)
              }
              allowClear={false}
              disabledDate={(current) =>
                current && current.valueOf() > dayjs().endOf("day").valueOf()
              }
              className="date-filter"
            />
          </div>
        </div>

        <Table
          dataSource={batchRuns}
          columns={batchColumns}
          rowKey="runId"
          loading={isBatchLoading}
          size="small"
          className="batch-table"
          pagination={{ pageSize: 18, showSizeChanger: false }}
          locale={{ emptyText: "No batch runs yet. Upload a file and resend to get started." }}
        />

        <Drawer
          title={
            <div className="uid-drawer-title">
              <div className="uid-drawer-title-left">
                <span>RUN ID </span>
                {activeRun?.runId ? <Tag className="uid-drawer-run-id-tag">{activeRun.runId}</Tag> : null}
                {activeRun ? <Tag color={statusColors[activeRun.status]}>{activeRun.status}</Tag> : null}
              </div>
              <Button
                type="text"
                icon={<ReloadOutlined spin={isRefreshingActiveRun} />}
                onClick={refreshActiveRunStatus}
                loading={isRefreshingActiveRun}
                className="uid-drawer-refresh-btn"
              >
                Refresh
              </Button>
            </div>
          }
          open={isUidDrawerOpen}
          onClose={closeUidDrawer}
          afterOpenChange={(open) => {
            if (open) {
              resetUidDrawerScroll();
            }
          }}
          destroyOnClose
          placement="right"
          width="50%"
          getContainer={() => pageContainerRef.current ?? document.body}
          rootStyle={{ position: "absolute", inset: 0 }}
          className="uid-drawer"
          styles={{
            mask: {
              backdropFilter: "blur(3px)",
              background: "rgba(15, 23, 42, 0.2)",
            },
          }}
        >
          <div className="uid-drawer-content">
            <div className="uid-drawer-meta">
              <div className="meta-row">
                <span className="ran-by-cell">
                  <span className="avatar">{activeRun?.ranByInitials}</span>
                  {activeRun?.ranBy}
                </span>
               
              </div>

              <div className="meta-progress">
                <div className="progress-cell">
                  <div className="progress-bar-track">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${activeRun?.progress ?? 0}%` }}
                    />
                  </div>
                </div>
                <span className="progress-pct">{activeRun?.progress ?? 0}% processed</span>
              </div>

              <div className="meta-results">
                <span className="result-badge success">
                  <CheckCircleOutlined /> {activeRun?.results.success ?? 0}
                </span>
                <span className="result-badge failed">
                  <CloseCircleOutlined /> {(activeRun?.results.failed ?? 0) + (activeRun?.results.warning ?? 0)}
                </span>
                
                <span className="result-badge pending">
                  <CloseCircleOutlined /> {(activeRun?.totalUids ?? 0) - ((activeRun?.results.success ?? 0) + (activeRun?.results.failed ?? 0) + (activeRun?.results.warning ?? 0))}
                </span>
              </div>
            </div>

            <div className="uid-drawer-filters">
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search UUID or details..."
                value={uidSearchTerm}
                onChange={(e) => setUidSearchTerm(e.target.value)}
                className="uid-search"
                allowClear
              />
            </div>

            <Table
              dataSource={filteredUidRows}
              columns={uidColumns}
              rowKey={(record, index) => `${record.uid}-${index}`}
              pagination={{ pageSize: 8, showSizeChanger: false }}
              size="small"
              className="uid-table"
              locale={{ emptyText: "No UID records found for selected filters." }}
            />
          </div>
        </Drawer>
      </div>
    </div>
  );
};
